import { Injectable } from '@angular/core';

/**
 * Pending SSO-link handoff for the account-setup flow (first-time link).
 *
 * WHY SESSIONSTORAGE (the one deliberate deviation from the "setup token
 * never touches web storage" rule): the Microsoft path is a FULL-PAGE
 * redirect (MSAL loginRedirect → Azure AD → back to the app root). Module
 * memory dies with the page, so the in-memory MfaChallengeBridge pattern
 * cannot carry the setup token across it. sessionStorage survives the
 * redirect round-trip within the same tab and is scoped to it — the token
 * is single-use server-side and consumed (removed) on first read, so the
 * exposure window is one redirect round-trip in one tab.
 *
 * The Google path does NOT use this bridge for transport: GIS is a
 * popup/callback flow in the same page context, so the credential arrives
 * without any navigation. The bridge is still used to mark the intent
 * ("this setup session is linking SSO") so the component knows which
 * credential path to submit.
 *
 * Shape: { token: setupToken, provider: 'microsoft' | 'google' }.
 * The setup token is NEVER logged and removed immediately on consumption.
 */
const PENDING_KEY = 'parent-portal-sso-link-pending';
const CREDENTIAL_KEY = 'parent-portal-sso-link-credential';
/** A staged link older than this is stale — the boot hook treats it as
 * absent so a forgotten entry cannot hijack later login redirects
 * (observed live 2026-09-11: a stale pending link from setup testing
 * diverted the LOGIN redirect's credential into the capture path, so the
 * exchange never ran and the login silently did nothing). */
const PENDING_TTL_MS = 10 * 60 * 1000;

export interface PendingSsoLink {
  token: string;
  provider: 'microsoft' | 'google';
  stagedAt: number;
}

@Injectable({ providedIn: 'root' })
export class SsoLinkBridge {
  /** Stage the pending link before an SSO redirect (Microsoft) or prompt (Google). */
  stage(token: string, provider: 'microsoft' | 'google'): void {
    try {
      sessionStorage.setItem(
        PENDING_KEY,
        JSON.stringify({ token, provider, stagedAt: Date.now() }),
      );
    } catch {
      // Storage unavailable (private mode) — the link cannot survive a
      // redirect; the caller's flow will fail visibly rather than silently.
    }
  }

  /**
   * Capture the SSO ID token from the redirect round-trip.
   *
   * Called by AuthService's boot redirect hook — which is subscribed to the
   * redirect result BEFORE any route component — so the credential survives
   * even when the resume component subscribes after the emission (the
   * two-page-load race: MSAL consumes the hash on the first load, and a
   * second load resolves null). One-shot read on the resume side.
   */
  captureCredential(ssoIdToken: string): void {
    try {
      sessionStorage.setItem(CREDENTIAL_KEY, ssoIdToken);
    } catch {
      // Storage unavailable — the resume's ReplaySubject fallback still applies.
    }
  }

  /** Consume the captured credential (one-shot; removes the entry). */
  consumeCredential(): string | null {
    try {
      const value = sessionStorage.getItem(CREDENTIAL_KEY);
      if (value) {
        sessionStorage.removeItem(CREDENTIAL_KEY);
        return value;
      }
      return null;
    } catch {
      return null;
    }
  }

  /** Read the pending link WITHOUT consuming it (used by the root guard). */
  peek(): PendingSsoLink | null {
    return this.read();
  }

  /** Consume the pending link (one-shot read; removes the entry). */
  consume(): PendingSsoLink | null {
    const pending = this.read();
    this.clear();
    return pending;
  }

  clear(): void {
    try {
      sessionStorage.removeItem(PENDING_KEY);
      sessionStorage.removeItem(CREDENTIAL_KEY);
    } catch {
      // Storage unavailable — nothing to clear.
    }
  }

  private read(): PendingSsoLink | null {
    try {
      const raw = sessionStorage.getItem(PENDING_KEY);
      if (!raw) {
        return null;
      }
      const parsed: unknown = JSON.parse(raw);
      if (
        parsed !== null &&
        typeof parsed === 'object' &&
        'token' in parsed &&
        'provider' in parsed &&
        typeof (parsed as { token: unknown }).token === 'string' &&
        ((parsed as { provider: unknown }).provider === 'microsoft' ||
          (parsed as { provider: unknown }).provider === 'google')
      ) {
        const pending = parsed as PendingSsoLink;
        // TTL: a stale entry (older than PENDING_TTL_MS) is treated as
        // absent and removed — a forgotten link must not divert later
        // login redirects into the capture path.
        const stagedAtRaw: unknown = (parsed as { stagedAt?: unknown }).stagedAt;
        const stagedAt = typeof stagedAtRaw === 'number' ? stagedAtRaw : 0;
        if (Date.now() - stagedAt > PENDING_TTL_MS) {
          this.clear();
          return null;
        }
        return pending;
      }
      // Malformed entry — treat as absent.
      this.clear();
      return null;
    } catch {
      return null;
    }
  }
}